import * as Sentry from "@sentry/vue";

import { getMaterial } from "@/api/services/Materials";
import { getConstructionById } from "@/api/services/Constructions";
import { isConstructionMaterial } from "@/utils/constructionMaterial";
import type { Material } from "@/types/Views/MaterialDatabase/Material";

// Where a mapped EPD's *declared* values come from (RTLCA-5544).
//
// A mapping payload from `GET api/Mapping/buildingproject/{id}/materials` cannot be used for this:
// the API deliberately overwrites `Mapping.LCIAResults` with the material's project-scaled
// contribution (BuildingMaterialService.CalculateScaledMaterialsAndConstructions ->
// ProjectMaterialEmissionCalculator), because the dashboard and this modal's KPI strip need those
// numbers. So anything that wants "the EPD as declared" has to re-read it from the EPD service,
// which knows nothing about projects, areas or consideration periods.
//
// The routing matters. `GET api/construction/{id}` throws ItemNotFoundException for anything that
// isn't a construction (ConstructionService.GetById), and `api/construction/v2/{id}` is the editable
// upsert shape that carries no lciaResults at all — so a construction must go through the non-v2
// construction endpoint and a plain material through the material one. (Since RTLCA-4166
// `api/material/{id}` composes a construction's totals the same way, so the two now agree on the
// numbers — the split below is still needed for the 404, not for the figures.)

/**
 * Re-reads a mapped EPD from the EPD service so it can be shown on its own declared basis.
 *
 * Returns `null` — never throws — when there is nothing to read (no id), when the call fails, or
 * when the body comes back empty (`getConstructionById` resolves `undefined` on a non-200). Callers
 * are expected to surface that as an error rather than falling back to the mapping payload: showing
 * project-scaled numbers as declared values is the defect this exists to fix.
 */
export const getDeclaredEpdSource = async (mapping: Material): Promise<Material | null> => {
  if (!mapping?.id) return null;

  try {
    const source = isConstructionMaterial(mapping) ? await getConstructionById(mapping.id) : await getMaterial(mapping.id);

    return (source as Material | undefined) ?? null;
  } catch (error: unknown) {
    Sentry.captureException(error);
    return null;
  }
};
